import { useCallback, useState } from 'react'
import type { Booking } from 'repositories/BerthApi/types/booking'
import { getPreviousBooking } from 'repositories/BerthApi/getPreviousBooking'

import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'
import { BOOKING_STATUS, BOOKING_STATUS_LABEL, SHORTEN_TIME_FLAG } from 'constants/Booking'
import { getApiErrorMessage } from 'containers/SpBerthManagement/utils/berthManagementHelpers'

import { useUpdateBookingStatus } from './useUpdateBookingStatus'
import { useUpdateRepeatStatus } from './useUpdateRepeatStatus'

type PendingAction = {
  bookingId: string
  nextStatus: number
} | null

type ForceFinishState = {
  previousBookingId: string
  previousBooking: Booking
  bookingId: string
  nextStatus: number
} | null

type Args = {
  tenantId?: string | null
  bookingsMap: Record<string, Booking>
  onNotify: (payload: SnackbarPayload) => void
}

type Result = {
  pendingAction: PendingAction
  forceFinish: ForceFinishState
  isDialogLoading: boolean
  handleStatusChange: (bookingId: string, nextStatus: number) => void
  handleConfirmOk: () => Promise<void>
  handleConfirmCancel: () => void
  handleForceFinishOk: () => void
  handleForceFinishCancel: () => void
}

const ACTIVE_STATUSES = [BOOKING_STATUS.Arrival, BOOKING_STATUS.Calling, BOOKING_STATUS.Working]

export const getStatusLabel = (status: number | null | undefined): string => {
  if (status == null) return ''
  return BOOKING_STATUS_LABEL[status as keyof typeof BOOKING_STATUS_LABEL] ?? ''
}

export const useBookingStatusChange = ({ tenantId, bookingsMap, onNotify }: Args): Result => {
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const [forceFinish, setForceFinish] = useState<ForceFinishState>(null)

  const updateStatus = useUpdateBookingStatus()
  const updateRepeatStatus = useUpdateRepeatStatus()

  const handleStatusChange = useCallback((bookingId: string, nextStatus: number) => {
    setPendingAction({ bookingId, nextStatus })
  }, [])

  /** 通常予約のステータス更新 */
  const executeNormalStatusUpdate = useCallback(
    (bookingId: string, nextStatus: number, prevStatus: number | null) => {
      if (!tenantId) return

      const isFinish = nextStatus === BOOKING_STATUS.Finish

      updateStatus.mutate(
        {
          bookingId,
          status: nextStatus,
          tenantId,
          ...(isFinish && { shortenTimeFlag: SHORTEN_TIME_FLAG.ON }),
        },
        {
          onSuccess: () => {
            setPendingAction(null)
            const prevLabel = getStatusLabel(prevStatus)
            const nextLabel = getStatusLabel(nextStatus)
            onNotify({
              message: `ステータスを${prevLabel}から${nextLabel}に更新しました。`,
              variant: 'success',
            })
          },
          onError: (error: unknown) => {
            setPendingAction(null)
            onNotify({ message: getApiErrorMessage(error), variant: 'error' })
          },
        }
      )
    },
    [tenantId, updateStatus, onNotify]
  )

  /** 繰り返し予約のステータス更新 */
  const executeRepeatStatusUpdate = useCallback(
    (booking: Booking, nextStatus: number, prevStatus: number | null) => {
      if (!tenantId || !booking.repeatId) return

      updateRepeatStatus.mutate(
        {
          repeatId: booking.repeatId,
          // NOTE: 調整中の繰り返し予約は bookingId が null。PC版も null のまま投げているためそれに合わせる
          bookingId: booking.bookingId,
          tenantId,
          startDate: booking.startDate ?? '',
          startTime: booking.startTime ?? '',
          endDate: booking.endDate ?? '',
          endTime: booking.endTime ?? '',
          status: nextStatus,
          blacklistId: booking.repeatBlacklistId ?? null,
        },
        {
          onSuccess: () => {
            setPendingAction(null)
            const prevLabel = getStatusLabel(prevStatus)
            const nextLabel = getStatusLabel(nextStatus)
            onNotify({
              message: `ステータスを${prevLabel}から${nextLabel}に更新しました。`,
              variant: 'success',
            })
          },
          onError: (error: unknown) => {
            setPendingAction(null)
            onNotify({ message: getApiErrorMessage(error), variant: 'error' })
          },
        }
      )
    },
    [tenantId, updateRepeatStatus, onNotify]
  )

  /** ステータス更新の実行（通常/繰り返しを振り分け） */
  const executeStatusUpdate = useCallback(
    (bookingId: string, nextStatus: number) => {
      const booking = bookingsMap[bookingId]
      if (!booking) return

      // 未インスタンス化の繰り返し予約は API レスポンスで status が返らないため、未割当として扱う
      const prevStatus =
        booking.status ??
        (booking.repeatId && !booking.bookingId ? BOOKING_STATUS.Unassigned : null)

      if (booking.repeatId) {
        executeRepeatStatusUpdate(booking, nextStatus, prevStatus)
      } else {
        executeNormalStatusUpdate(bookingId, nextStatus, prevStatus)
      }
    },
    [bookingsMap, executeNormalStatusUpdate, executeRepeatStatusUpdate]
  )

  /** 前の予約チェック */
  const checkPreviousBooking = useCallback(async (booking: Booking) => {
    if (!booking.bookingId) return null
    return getPreviousBooking(booking.bookingId)
  }, [])

  const handleConfirmOk = useCallback(async () => {
    if (!pendingAction || !tenantId) return

    const booking = bookingsMap[pendingAction.bookingId]
    if (!booking) return

    const needsPreviousCheck =
      pendingAction.nextStatus === BOOKING_STATUS.Calling ||
      pendingAction.nextStatus === BOOKING_STATUS.Working

    if (needsPreviousCheck) {
      try {
        const result = await checkPreviousBooking(booking)

        // BEの判定でブロック（繰り返し予約12時間チェックなど previousBooking が null のケースを含む）
        if (result?.canComplete === false && !result.previousBooking) {
          setPendingAction(null)
          onNotify({
            message: result.validationMessage || 'ステータス更新に失敗しました。',
            variant: 'error',
          })
          return
        }

        const prevBooking = result?.previousBooking

        if (result && prevBooking && prevBooking.bookingId) {
          // BEの判定でブロック
          if (result.canComplete === false) {
            setPendingAction(null)
            onNotify({
              message: result.validationMessage || 'ステータス更新に失敗しました。',
              variant: 'error',
            })
            return
          }

          // ステータスが受付済/呼出中/作業中 → 強制完了確認ダイアログ
          if (
            prevBooking.status != null &&
            ACTIVE_STATUSES.includes(prevBooking.status as (typeof ACTIVE_STATUSES)[number])
          ) {
            const prevBookingFull = bookingsMap[prevBooking.bookingId]
            setPendingAction(null)
            setForceFinish({
              previousBookingId: prevBooking.bookingId,
              previousBooking:
                prevBookingFull ??
                ({
                  bookingId: prevBooking.bookingId,
                  status: prevBooking.status,
                  startTime: prevBooking.startTime,
                  endTime: prevBooking.endTime,
                  startDate: prevBooking.startDate,
                  endDate: prevBooking.endDate,
                } as Booking),
              bookingId: pendingAction.bookingId,
              nextStatus: pendingAction.nextStatus,
            })
            return
          }
        }
      } catch {
        // チェックAPI自体が失敗した場合はステータス更新に進む
      }
    }

    executeStatusUpdate(pendingAction.bookingId, pendingAction.nextStatus)
  }, [pendingAction, tenantId, bookingsMap, checkPreviousBooking, executeStatusUpdate, onNotify])

  const handleConfirmCancel = useCallback(() => {
    setPendingAction(null)
  }, [])

  const handleForceFinishOk = useCallback(() => {
    if (!forceFinish || !tenantId) return

    // 前の予約を強制完了（通常予約用APIを使用。PC版も強制完了は update_status を使う）
    updateStatus.mutate(
      {
        bookingId: forceFinish.previousBookingId,
        status: BOOKING_STATUS.Finish,
        tenantId,
        shortenTimeFlag: SHORTEN_TIME_FLAG.ON,
      },
      {
        onSuccess: () => {
          const { bookingId, nextStatus } = forceFinish
          setForceFinish(null)
          executeStatusUpdate(bookingId, nextStatus)
        },
        onError: (error: unknown) => {
          setForceFinish(null)
          onNotify({ message: getApiErrorMessage(error), variant: 'error' })
        },
      }
    )
  }, [forceFinish, tenantId, updateStatus, executeStatusUpdate, onNotify])

  const handleForceFinishCancel = useCallback(() => {
    setForceFinish(null)
  }, [])

  const isDialogLoading = updateStatus.isPending || updateRepeatStatus.isPending

  return {
    pendingAction,
    forceFinish,
    isDialogLoading,
    handleStatusChange,
    handleConfirmOk,
    handleConfirmCancel,
    handleForceFinishOk,
    handleForceFinishCancel,
  }
}
